








import {
  Vue,
  Component,
  Prop,
} from "@/shared/vuepack";
import _ from "lodash";
import moment from "moment";
import { ChartDataItem, DrivewealthAccountPerformanceTimePeriod } from "@/shared/types";
import { formatCurrency } from "@/shared/currency";
import { GChart } from "vue-google-charts";
import { useThemeStore } from "@/shared/store/theme";
import { useDrivewealthAccountPerformanceStore } from "@/shared/store/drivewealthAccountPerformance";

@Component({
  components: {
    GChart
  }
})
export default class InvestmentsPerformanceLineChart extends Vue {
  drivewealthAccountPerformanceStore = useDrivewealthAccountPerformanceStore();
  themeStore = useThemeStore();
  manualXAxisLabelCutoff = 7;

  @Prop()
  strokeColor: number;

  @Prop()
  fillColor: string;

  @Prop()
  width: number;

  @Prop()
  height: number;

  @Prop()
  dataSet: ChartDataItem[];

  get getChartOptions (): any {
    return {
      fontName: this.themeStore.fontFace,
      annotations: {
        color: this.themeStore.tertiaryColor
      },
      titlePosition: "none",
      colors: [ "#B2BCBB" ],
      tooltip: {
        isHtml: true,
      },
      chartArea: {
        height: "85%",
      },
      hAxis: {
        baselineColor: "black",
        format: this.xAxisFormat,
        gridlines: {
          color: "transparent"
        },
        ticks: this.xAxisLabels
      },
      vAxis: {
        minValue: 0,
        baselineColor: "black",
        format: "currency",
        gridlines: {
          color: "transparent",
        },
        minorGridlines: {
          color: "transparent",
        }
      },
      pointSize: 0,
      height: this.height,
      legend: { position: "none" },
      enableInteractivity: true,
    };
  }

  get xAxisFormat (): string {
    switch ( this.drivewealthAccountPerformanceStore.selectedTimePeriod) {
      case DrivewealthAccountPerformanceTimePeriod.Months1:
        return "dd";
      case DrivewealthAccountPerformanceTimePeriod.Months3:
        return "MM/dd";
      case DrivewealthAccountPerformanceTimePeriod.Months6:
        return "MM/dd";
      case DrivewealthAccountPerformanceTimePeriod.YearToDate:
        return "MM/dd";
      case DrivewealthAccountPerformanceTimePeriod.Years3:
        return "MM/yy";
      case DrivewealthAccountPerformanceTimePeriod.Years5:
        return "MM/yy";
      default:
        return "MM/dd";
    }
  }

  tooltipFormatter (data: {
    balance: number, date: string, employerAmount?: number,
    employeeRecurringAmount?: number, employeeOneTimeAmount?: number,
    transactionSummaries: { amount: number, description: string }[]
  }): string {

    const mainMarkup = `<div style="font-weight: bold; margin-bottom: 3px;">
                          ${moment(data.date, "DD-MMM-YY").format("MMM DD")}
                        </div>
                        <div margin-bottom: 3px;">
                          Balance:
                        </div>
                        <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
                          ${formatCurrency(data.balance)}
                        </div>`;

    const tooltipHtml = `<div style="padding: 2px 10px; min-width: 120px;">
                          ${mainMarkup}
                        </div>`;

    return tooltipHtml;
  }

  contributionsTooltipFormatter (balance: number): string {

    const mainMarkup = `<div style="font-weight: bold; margin-bottom: 3px;">
                          Contributions Amount:
                        </div>
                        <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
                          ${formatCurrency(balance)}
                        </div>`;

    const tooltipHtml = `<div style="padding: 2px 10px; min-width: 120px;">
                          ${mainMarkup}
                        </div>`;

    return tooltipHtml;
  }

  formatChartDataItem (balanceItem: ChartDataItem): any {
    return [
      moment(balanceItem.date, "DD-MMM-YY").toDate(),
      balanceItem.balance,
      `stroke-color: ${this.strokeColor}; fill-color: ${this.fillColor}; fill-opacity: 0`,
      this.tooltipFormatter(balanceItem),
    ];
  }

  // If we have too few dates, google charts will try to interpolate the date value,
  // resulting in duplcated dates on the X axis. If there are too few, we need to set
  // the x-axis labels manually. Otherwise, let google do its thing.
  get xAxisLabels () {
    const distinctDates = _.uniq(this.chartData.map(x => x.date));

    return distinctDates.length >= this.manualXAxisLabelCutoff ?
        null : distinctDates.map(x => moment(x, "DD-MMM-YY").toDate());
  }

  get chartData () {
    return [ ...this.dataSet ];
  }

  get formattedChartData () {
    const formattedChartPoints = this.chartData.map(this.formatChartDataItem);

    return [
      [
        "Date",
        "Amount",
        { type: "string", role: "style" },
        {
          type: "string", role: "tooltip", p: {
            html: true
          }
        },
      ],
      ...formattedChartPoints
    ];
  }
}
